import React from "react";
import { cn } from "@/lib/utils";

export interface OutlineButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	/** Button label, e.g. "Välj kontaktperson". */
	label: React.ReactNode;
	/** Trailing icon node, e.g. a lucide `<ChevronDown size={15} />` or `<Paperclip size={14} />`. */
	icon?: React.ReactNode;
}

/**
 * Solid-bordered secondary action button — white background, gray border,
 * label with an optional trailing icon. The compact counterpart of the dashed
 * {@link AddButton} for actions that open a picker/dialog rather than create
 * something (e.g. "Välj kontaktperson", "Ladda upp dokument"). Domain-free —
 * pass the label/icon and an onClick. Restyle via `className` (merged with
 * `cn`). Forwards refs so it can be a Radix `asChild` trigger.
 */
const OutlineButton = React.forwardRef<HTMLButtonElement, OutlineButtonProps>(
	({ label, icon, className, type = "button", ...props }, ref) => (
		<button
			ref={ref}
			type={type}
			className={cn(
				"inline-flex items-center gap-1.5 rounded-md border border-solid border-gray-300 bg-white px-2.5 py-1 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:pointer-events-none disabled:opacity-50",
				className
			)}
			{...props}
		>
			{label}
			{icon}
		</button>
	)
);

OutlineButton.displayName = "OutlineButton";

export default OutlineButton;
